/**
 * ESC/POS byte-stream builder for 58mm/80mm thermal receipt printers.
 * Pure functions — no DOM, no browser APIs. Returns Uint8Array ready to send over WebUSB / WebBluetooth.
 *
 * Reference: Epson TM-T88V Command Reference, Star CBM-1000 compatible subset.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

export const ALIGN = { LEFT: 0, CENTER: 1, RIGHT: 2 };
export const PAPER_WIDTH = {
  58: { columns: 32 }, // 58mm paper, ~32 chars at font A
  80: { columns: 48 }, // 80mm paper, ~48 chars at font A
};

/**
 * Encode a printable string using CP437. TextEncoder only supports UTF-8, so for
 * characters outside ASCII we fall back to '?'. Restaurants wanting full Unicode
 * need to switch code pages — covered by setCodePage(). ASCII + $ + common punct
 * handles ~99% of US receipts.
 */
function encodeAscii(str) {
  const bytes = [];
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    bytes.push(code < 0x80 ? code : 0x3f); // '?'
  }
  return new Uint8Array(bytes);
}

function concat(parts) {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export class ReceiptBuilder {
  constructor(paperWidth = 80) {
    this.parts = [];
    this.paperWidth = paperWidth;
    this.columns = PAPER_WIDTH[paperWidth]?.columns || 48;
    this.init();
  }

  init() {
    this.parts.push(new Uint8Array([ESC, 0x40])); // ESC @ — reset
    this.parts.push(new Uint8Array([ESC, 0x74, 0x00])); // ESC t 0 — CP437
    return this;
  }

  align(mode) {
    this.parts.push(new Uint8Array([ESC, 0x61, mode]));
    return this;
  }

  bold(on) {
    this.parts.push(new Uint8Array([ESC, 0x45, on ? 1 : 0]));
    return this;
  }

  /** GS ! n — size multipliers. width/height 1–8. */
  size(width = 1, height = 1) {
    const w = Math.max(1, Math.min(8, width)) - 1;
    const h = Math.max(1, Math.min(8, height)) - 1;
    const n = (w << 4) | h;
    this.parts.push(new Uint8Array([GS, 0x21, n]));
    return this;
  }

  text(str) {
    this.parts.push(encodeAscii(String(str ?? '')));
    return this;
  }

  line(str = '') {
    this.text(str);
    this.parts.push(new Uint8Array([LF]));
    return this;
  }

  feed(n = 1) {
    this.parts.push(new Uint8Array([ESC, 0x64, Math.max(0, Math.min(255, n))]));
    return this;
  }

  /** Full-width divider. kind: '-' or '=' */
  divider(kind = '-') {
    this.line(kind.repeat(this.columns));
    return this;
  }

  /**
   * Two-column row: left text + right text, padded to column width.
   * If left is too long, it truncates.
   */
  twoCol(left, right) {
    const l = String(left ?? '');
    const r = String(right ?? '');
    const pad = this.columns - l.length - r.length;
    if (pad < 1) {
      const maxLeft = Math.max(1, this.columns - r.length - 1);
      this.line(l.slice(0, maxLeft) + ' ' + r);
    } else {
      this.line(l + ' '.repeat(pad) + r);
    }
    return this;
  }

  /**
   * Item row: "Qty x Name ..... $price". Wraps long names across lines.
   */
  itemRow(qty, name, priceStr) {
    const prefix = `${qty} x `;
    const maxNameLen = this.columns - prefix.length - priceStr.length - 1;
    const nameStr = String(name ?? '');
    if (nameStr.length <= maxNameLen) {
      const pad = this.columns - prefix.length - nameStr.length - priceStr.length;
      this.line(prefix + nameStr + ' '.repeat(Math.max(1, pad)) + priceStr);
    } else {
      // First line: qty + name slice + price
      const firstSlice = nameStr.slice(0, maxNameLen);
      const rest = nameStr.slice(maxNameLen);
      const pad = this.columns - prefix.length - firstSlice.length - priceStr.length;
      this.line(prefix + firstSlice + ' '.repeat(Math.max(1, pad)) + priceStr);
      // Continuation lines indented under the name column
      const indent = ' '.repeat(prefix.length);
      const contWidth = this.columns - prefix.length;
      for (let i = 0; i < rest.length; i += contWidth) {
        this.line(indent + rest.slice(i, i + contWidth));
      }
    }
    return this;
  }

  /** GS V m — cut. 0=full, 1=partial. Most printers treat both similarly. */
  cut(partial = true) {
    this.parts.push(new Uint8Array([GS, 0x56, partial ? 1 : 0]));
    return this;
  }

  /** ESC p m t1 t2 — open cash drawer pulse. m=0 drawer 1. */
  drawerKick() {
    this.parts.push(new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xff]));
    return this;
  }

  build() {
    return concat(this.parts);
  }
}

/**
 * Build a complete receipt from a receiptData object (same shape used by ReceiptModal).
 * Returns Uint8Array ready to print.
 */
export function buildReceipt(receiptData, options = {}) {
  const { paperWidth = 80, autoCut = true } = options;
  const b = new ReceiptBuilder(paperWidth);

  const {
    restaurantName,
    restaurantAddress,
    restaurantPhone,
    orderNumbers,
    items = [],
    subtotal = 0,
    taxRate = 0,
    taxAmount = 0,
    discountAmount = 0,
    discountType = 'amount',
    promoCode,
    tipAmount = 0,
    total = 0,
    paymentMethod,
    tableNumbers,
    paidAt,
  } = receiptData || {};

  // Header
  b.align(ALIGN.CENTER).size(2, 2).bold(true).line(restaurantName || 'Restaurant').bold(false).size(1, 1);
  if (restaurantAddress) b.line(restaurantAddress);
  if (restaurantPhone) b.line(`Tel: ${restaurantPhone}`);
  b.feed(1);
  b.align(ALIGN.LEFT).divider('-');

  // Meta
  const orderStr = (orderNumbers && orderNumbers.length) ? orderNumbers.join(', ') : 'N/A';
  b.line(`Order: ${orderStr}`);
  const dateStr = formatDate(paidAt);
  b.line(`Date:  ${dateStr}`);
  if (tableNumbers && tableNumbers.length) {
    b.line(`Table: ${tableNumbers.join(', ')}`);
  }
  b.divider('-');

  // Items
  for (const item of items) {
    const qty = Number(item.quantity) || 1;
    const price = Number(item.price) || 0;
    const lineTotal = (qty * price).toFixed(2);
    b.itemRow(qty, item.name || 'Item', `$${lineTotal}`);
  }
  b.divider('-');

  // Totals
  b.twoCol('Subtotal', `$${num(subtotal)}`);
  if (discountAmount && Number(discountAmount) > 0) {
    const label = discountType === 'percentage'
      ? `Discount (${discountAmount}%${promoCode ? ' ' + promoCode : ''})`
      : `Discount${promoCode ? ' (' + promoCode + ')' : ''}`;
    const value = discountType === 'percentage'
      ? (Number(subtotal) * Number(discountAmount) / 100)
      : Number(discountAmount);
    b.twoCol(label, `-$${num(value)}`);
  }
  b.twoCol(`Tax (${taxRate || 0}%)`, `$${num(taxAmount)}`);
  if (tipAmount && Number(tipAmount) > 0) {
    b.twoCol('Tip', `$${num(tipAmount)}`);
  }
  b.divider('=');
  b.size(2, 2).bold(true).twoColWide('TOTAL', `$${num(total)}`).bold(false).size(1, 1);
  b.divider('-');

  // Payment
  b.align(ALIGN.CENTER).line(`Paid via ${paymentMethodLabel(paymentMethod)}`);
  b.align(ALIGN.LEFT).divider('-');

  // Footer
  b.align(ALIGN.CENTER).bold(true).line('Thank you for dining with us!').bold(false);
  b.feed(1);
  b.line('Powered by Koda Carte');
  b.align(ALIGN.LEFT);

  // Trailer: feed + cut
  b.feed(3);
  if (autoCut) b.cut(true);

  return b.build();
}

/** Wide two-col for the big TOTAL row (uses half the columns because each char is 2x wide). */
ReceiptBuilder.prototype.twoColWide = function (left, right) {
  const wideCols = Math.floor(this.columns / 2);
  const l = String(left ?? '');
  const r = String(right ?? '');
  const pad = wideCols - l.length - r.length;
  if (pad < 1) {
    this.line(l + ' ' + r);
  } else {
    this.line(l + ' '.repeat(pad) + r);
  }
  return this;
};

/**
 * Build a simple test-print receipt used by the setup wizard.
 */
export function buildTestPrint(restaurantName, paperWidth = 80) {
  const b = new ReceiptBuilder(paperWidth);
  b.align(ALIGN.CENTER).size(2, 2).bold(true).line('PRINTER TEST').bold(false).size(1, 1);
  b.feed(1);
  b.align(ALIGN.CENTER).line(restaurantName || 'Koda Carte');
  b.line(new Date().toLocaleString());
  b.feed(1);
  b.align(ALIGN.LEFT).divider('-');
  b.line('If you can read this, your');
  b.line('thermal printer is connected');
  b.line('and working correctly.');
  b.divider('-');
  b.twoCol('Paper width', `${paperWidth}mm`);
  b.twoCol('Columns', String(b.columns));
  b.divider('-');
  b.align(ALIGN.CENTER).line('Powered by Koda Carte');
  b.feed(3);
  b.cut(true);
  return b.build();
}

function num(v) {
  const n = Number(v) || 0;
  return n.toFixed(2);
}

function formatDate(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) return new Date().toLocaleString();
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function paymentMethodLabel(method) {
  switch (method) {
    case 'cash': return 'Cash';
    case 'card': return 'Credit/Debit Card';
    case 'terminal': return 'Card Terminal';
    default: return method || 'N/A';
  }
}
